"use client"

import { useState, useEffect } from "react"
import { X, Palette, ImageIcon, Type, Layers2, MousePointer, Shirt, RotateCcw, ChevronDown, ChevronUp } from "lucide-react"
import { cn } from "../../../lib/utils"

// ─── Step data ────────────────────────────────────────────────────────────────

const STEPS = [
  {
    icon: <Shirt className="w-5 h-5" />,
    color: "from-blue-500/20 to-blue-600/10 border-blue-500/30",
    iconColor: "text-blue-400",
    title: "Escolha o modelo",
    desc: "No painel esquerdo (desktop) ou menu lateral (mobile), escolha a modelagem e o design do uniforme.",
    tip: "Trocar o modelo reseta as edições anteriores.",
  },
  {
    icon: <Palette className="w-5 h-5" />,
    color: "from-emerald-500/20 to-emerald-600/10 border-emerald-500/30",
    iconColor: "text-emerald-400",
    title: "Personalize as cores",
    desc: "Na aba Cores, cada parte do uniforme (camisa, manga, calção, gola...) tem suas cores identificadas automaticamente. Clique numa cor para trocá-la pela tabela de cores disponível.",
    tip: "A tabela de cores é definida pelo seu fornecedor.",
  },
  {
    icon: <ImageIcon className="w-5 h-5" />,
    color: "from-violet-500/20 to-violet-600/10 border-violet-500/30",
    iconColor: "text-violet-400",
    title: "Adicione logos",
    desc: "Na aba Logo, faça upload da imagem do escudo ou logo. Após o upload, clique na parte do uniforme onde quer posicioná-la. Use os controles de tamanho e rotação para ajustar.",
    tip: "PNG com fundo transparente fica melhor.",
  },
  {
    icon: <Type className="w-5 h-5" />,
    color: "from-amber-500/20 to-amber-600/10 border-amber-500/30",
    iconColor: "text-amber-400",
    title: "Nome e número",
    desc: "Na aba Nome, preencha o nome e número do jogador. Para textos livres, clique em Adicionar texto, escolha fonte e cor, e clique no uniforme para posicionar.",
    tip: "Você pode arrastar os textos diretamente no modelo 3D.",
  },
  {
    icon: <Layers2 className="w-5 h-5" />,
    color: "from-rose-500/20 to-rose-600/10 border-rose-500/30",
    iconColor: "text-rose-400",
    title: "Estampas e cliparts",
    desc: "Na aba Estampas, navegue pelas categorias e escolha um clipart. Clique no uniforme para posicionar. Você pode escalar, rotacionar e remover cada estampa individualmente.",
    tip: "Centenas de cliparts disponíveis por categoria.",
  },
  {
    icon: <MousePointer className="w-5 h-5" />,
    color: "from-cyan-500/20 to-cyan-600/10 border-cyan-500/30",
    iconColor: "text-cyan-400",
    title: "Reposicione arrastando",
    desc: "Logos, textos e estampas podem ser reposicionados diretamente no modelo 3D. Toque e arraste o elemento para movê-lo para outro local.",
    tip: "Abra o painel de edição do elemento antes de arrastar.",
  },
  {
    icon: <RotateCcw className="w-5 h-5" />,
    color: "from-orange-500/20 to-orange-600/10 border-orange-500/30",
    iconColor: "text-orange-400",
    title: "Resetar e limpar",
    desc: "Use Resetar para voltar ao estado inicial do uniforme. Use Limpar para remover todas as edições (cores, logos e textos) e começar do zero com o mesmo modelo.",
    tip: "Resetar também redefine as cores para o padrão.",
  },
]

// ─── Desktop step card ────────────────────────────────────────────────────────

function StepCard({ step, index }: { step: typeof STEPS[0]; index: number }) {
  return (
    <div className={cn(
      "rounded-2xl border bg-gradient-to-br p-4 flex gap-3 transition-all duration-200 hover:scale-[1.01]",
      step.color
    )}>
      <div className={cn(
        "w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-black/20",
        step.iconColor
      )}>
        {step.icon}
      </div>
      <div className="space-y-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest">
            {String(index + 1).padStart(2, "0")}
          </span>
          <p className="text-sm font-semibold text-foreground">{step.title}</p>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">{step.desc}</p>
        <p className="text-[10px] text-muted-foreground/60 italic mt-1">💡 {step.tip}</p>
      </div>
    </div>
  )
}

// ─── Mobile accordion step ────────────────────────────────────────────────────

function StepAccordion({ step, index }: { step: typeof STEPS[0]; index: number }) {
  const [open, setOpen] = useState(index === 0)

  return (
    <div className={cn("rounded-2xl border bg-gradient-to-br overflow-hidden", step.color)}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 p-3.5 touch-manipulation"
      >
        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-black/20", step.iconColor)}>
          {step.icon}
        </div>
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-bold text-muted-foreground/60 uppercase tracking-widest">
              {String(index + 1).padStart(2, "0")}
            </span>
            <p className="text-sm font-semibold text-foreground">{step.title}</p>
          </div>
        </div>
        {open
          ? <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        }
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2 animate-in slide-in-from-top-2 duration-200">
          <p className="text-xs text-muted-foreground leading-relaxed">{step.desc}</p>
          <p className="text-[10px] text-muted-foreground/60 italic">💡 {step.tip}</p>
        </div>
      )}
    </div>
  )
}

// ─── Main HelpModal ───────────────────────────────────────────────────────────

export function HelpModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[200]"
        onClick={onClose}
      />

      {/* ── Desktop modal (centered, scrollable) ── */}
      <div className="hidden lg:flex fixed inset-0 z-[201] items-center justify-center p-6">
        <div className="relative w-full max-w-3xl max-h-[90vh] flex flex-col glass-strong border border-border/50 rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 fade-in duration-200">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-border/30 shrink-0">
            <div>
              <h2 className="text-lg font-bold text-foreground">Como personalizar</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Siga os passos para criar seu uniforme</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-xl bg-secondary/60 hover:bg-secondary flex items-center justify-center transition-all hover:scale-105"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          {/* Steps grid — 2 columns */}
          <div className="overflow-y-auto p-6 custom-scrollbar">
            <div className="grid grid-cols-2 gap-3">
              {STEPS.map((step, i) => (
                <StepCard key={i} step={step} index={i} />
              ))}
            </div>

            {/* Footer */}
            <div className="mt-5 rounded-2xl bg-primary/10 border border-primary/20 px-4 py-3 text-center">
              <p className="text-xs text-muted-foreground">
                Dúvidas? Fale com a gente pelo{" "}
                <a href="https://wa.me/5562928117127" target="_blank" rel="noopener noreferrer"
                  className="text-primary font-semibold hover:underline">WhatsApp</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile modal (bottom sheet) ── */}
      <div className="lg:hidden fixed inset-x-0 bottom-0 z-[201] flex flex-col glass-strong border-t border-border/50 rounded-t-3xl shadow-2xl max-h-[85vh] animate-in slide-in-from-bottom-4 duration-300">

        {/* Drag handle */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0">
          <div className="w-10 h-1 rounded-full bg-border absolute left-1/2 -translate-x-1/2 top-3" />
          <h2 className="text-base font-bold text-foreground">Como personalizar</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl bg-secondary/60 hover:bg-secondary flex items-center justify-center transition-all"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Accordion steps */}
        <div className="overflow-y-auto px-4 pb-6 space-y-2 mt-2">
          {STEPS.map((step, i) => (
            <StepAccordion key={i} step={step} index={i} />
          ))}

          {/* Footer */}
          <div className="rounded-2xl bg-primary/10 border border-primary/20 px-4 py-3 text-center mt-2">
            <p className="text-xs text-muted-foreground">
              Dúvidas? Fale pelo{" "}
              <a href="https://wa.me/5562928117127" target="_blank" rel="noopener noreferrer"
                className="text-primary font-semibold hover:underline">WhatsApp</a>
            </p>
          </div>
        </div>
      </div>
    </>
  )
}